// Client for the Jack Lingo MLS proxy (Bright MLS data) exposed by the
// Cloudflare worker at lesliesavage.com under /api/jl/*.
// Ported from kristinalingo/kjrealtor/lib/jlApi.ts — wire format verified
// against the live endpoint 2026-08-09 (CORS: access-control-allow-origin *).
//
// debeach has no own worker, so the base defaults to the production worker
// origin. Override with VITE_JL_API_BASE if a dedicated proxy is deployed.

export const JL_ORIGIN = 'https://www.jacklingo.com'

export const JL_API_BASE = (
  (import.meta.env.VITE_JL_API_BASE as string | undefined) ||
  'https://www.lesliesavage.com'
).replace(/\/$/, '')

// P3.1 launch guard: a production bundle must never silently free-ride on
// another client's worker. Fail the build loudly instead.
if (import.meta.env.PROD && JL_API_BASE.includes('lesliesavage')) {
  throw new Error(
    'VITE_JL_API_BASE is unset in this production build and would default to ' +
      'lesliesavage.com. Set VITE_JL_API_BASE (or deploy the debeach worker ' +
      'same-origin) before shipping.'
  )
}

// Sussex County coastal bounds (Fenwick Island → Lewes/Cape Henlopen).
export const SUSSEX_BOUNDS: Bounds = {
  swLat: 38.45,
  neLat: 38.9,
  swLng: -75.45,
  neLng: -74.95,
}

export interface MapMarker {
  id: number
  lat: number
  lng: number
  price: number
}

export interface ListingDetail {
  id: number
  listingId: string // MLS number, e.g. DESU2098250
  street: string
  city: string
  state: string
  postalCode: string
  subdivision: string
  price: number
  priceDisplay: string
  bed: string
  bath: string
  sqFtTotal: string
  daysListedText: string
  listOfficeName: string
  imageUrl: string // path relative to JL_ORIGIN
  statusTagsHtml?: string
}

export interface Bounds {
  swLat: number
  neLat: number
  swLng: number
  neLng: number
}

export interface ListingFilters {
  priceMin?: string | number
  priceMax?: string | number
  bed?: string | number
  bath?: string | number
  propertyType?: string[]
  mlsstatus?: string
  search?: string
}

export const PROPERTY_TYPES = [
  'Residential',
  'Condo',
  'Land',
  'Multi-Family',
  'Commercial',
  'Farm',
]

export const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Active Under Contract', label: 'Under Contract' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Coming Soon', label: 'New Listings' },
]

// Hide leases and nominal/placeholder prices ($0–$99 stubs) below this floor.
export const PRICE_FLOOR = 10_000
const NEW_LISTING_DAYS = 30

export const PRICE_STEPS = [
  100_000, 200_000, 300_000, 400_000, 500_000, 600_000, 750_000, 1_000_000,
  1_500_000, 2_000_000, 3_000_000, 5_000_000,
]

// Fetch the price pins for a viewport/region. Throws on network/HTTP errors so
// callers can drive their own error UI (and ignore stale responses).
export async function fetchMarkers(
  bounds: Bounds,
  filters?: ListingFilters,
  signal?: AbortSignal
): Promise<MapMarker[]> {
  const res = await fetch(buildUrl('markers', bounds, filters), { signal })
  if (!res.ok) throw new Error(`markers ${res.status}`)
  const data = await res.json()
  const places = Array.isArray(data?.places) ? (data.places as MapMarker[]) : []
  // Markers only carry a price (no lease flag / listing age), so the floor is
  // the only lever here — it removes the $0–$99 stubs and rental rents.
  return places.filter((p) => Number(p.price) >= PRICE_FLOOR)
}

export function formatPrice(price: number): string {
  if (!Number.isFinite(price) || price <= 0) return '—'
  if (price >= 1_000_000) {
    const m = price / 1_000_000
    return `$${m % 1 === 0 ? m.toFixed(0) : m.toFixed(1)}M`
  }
  if (price >= 1_000) return `$${Math.round(price / 1_000)}K`
  return `$${price}`
}

export function buildFilterParams(f: ListingFilters): Record<string, string> {
  const out: Record<string, string> = {}
  const search = f.search?.toString().trim()
  if (search) out.search = search
  const userMin = Number(f.priceMin) || 0
  const effMin = Math.max(userMin, PRICE_FLOOR)
  const max = f.priceMax?.toString().trim() ?? ''
  out.price = `${effMin}-${max}`
  const bed = f.bed?.toString().trim()
  if (bed) out.bed = bed
  const bath = f.bath?.toString().trim()
  if (bath) out.bath = bath
  if (f.propertyType && f.propertyType.length)
    out.propertyType = f.propertyType.join(',')
  if (f.mlsstatus) out.mlsstatus = f.mlsstatus
  return out
}

function boundsParams(b: Bounds): URLSearchParams {
  return new URLSearchParams({
    swLat: String(b.swLat),
    neLat: String(b.neLat),
    swLng: String(b.swLng),
    neLng: String(b.neLng),
  })
}

function buildUrl(
  endpoint: 'markers' | 'listings',
  bounds: Bounds,
  filters?: ListingFilters
): string {
  const params = boundsParams(bounds)
  if (filters) {
    Object.entries(buildFilterParams(filters)).forEach(([k, v]) =>
      params.set(k, v)
    )
  }
  return `${JL_API_BASE}/api/jl/${endpoint}?${params.toString()}`
}

export async function fetchListings(
  bounds: Bounds,
  filters?: ListingFilters,
  signal?: AbortSignal
): Promise<ListingDetail[]> {
  const res = await fetch(buildUrl('listings', bounds, filters), { signal })
  if (!res.ok) throw new Error(`listings ${res.status}`)
  const data = await res.json()
  const items = Array.isArray(data?.items) ? (data.items as ListingDetail[]) : []
  return items.filter(isDisplayableListing)
}

export function isLease(item: { statusTagsHtml?: string }): boolean {
  return /lease/i.test(item.statusTagsHtml || '')
}

function daysOnMarket(text: string): number | null {
  if (!text) return null
  const t = text.toLowerCase()
  if (t.includes('today') || t.includes('hour') || t.includes('just')) return 0
  const day = t.match(/(\d+)\s*day/)
  if (day) return Number(day[1])
  const week = t.match(/(\d+)\s*week/)
  if (week) return Number(week[1]) * 7
  const month = t.match(/(\d+)\s*month/)
  if (month) return Number(month[1]) * 30
  return null
}

export function isDisplayableListing(item: ListingDetail): boolean {
  if (isLease(item)) return false
  if (Number(item.price) >= PRICE_FLOOR) return true
  const days = daysOnMarket(item.daysListedText)
  return days !== null && days < NEW_LISTING_DAYS
}

export function titleCase(str: string): string {
  return (str || '').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())
}

// Deep-link into Jack Lingo's map, pre-searched by MLS number.
export function jackLingoUrl(mls: string): string {
  if (!mls) return `${JL_ORIGIN}/real-estate/map`
  const sp = btoa(`search:${mls}`)
  return `${JL_ORIGIN}/real-estate/map?sp=${encodeURIComponent(sp)}`
}

// Listing photo URLs come back as paths relative to jacklingo.com.
export function listingPhotoUrl(path: string): string {
  if (!path) return '/media/listing-placeholder.svg'
  return path.startsWith('http') ? path : `${JL_ORIGIN}${path}`
}
